import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { X, Bell, Megaphone, Trophy, TrendingUp, Zap, Target, Crown, Award, Star, Check, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// Notification sound using Web Audio API - LOUD and attention-grabbing
const playNotificationSound = (type = 'broadcast') => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
    const now = audioContext.currentTime;
    
    // Play a loud multi-tone alert for both types
    const frequencies = type === 'motivation' 
      ? [523, 659, 784, 1047] // C5, E5, G5, C6 - uplifting arpeggio
      : [880, 1109, 1319, 880]; // A5, C#6, E6, A5 - urgent alert
    
    frequencies.forEach((freq, i) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(audioContext.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0, now + i * 0.12);
      gain.gain.linearRampToValueAtTime(0.6, now + i * 0.12 + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.01, now + i * 0.12 + 0.5);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.5);
    });
  } catch (e) {
    console.log('Audio not available');
  }
};

const NotificationPopup = () => {
  const { user, api } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [motivation, setMotivation] = useState(null);
  const [pendingBroadcasts, setPendingBroadcasts] = useState([]);
  const [acknowledging, setAcknowledging] = useState({});

  // Check for pending acknowledgements on mount and periodically
  const checkPendingAcknowledgements = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await api.get('/user/pending-acknowledgements');
      if (response.data.pending_broadcasts?.length > 0) {
        setPendingBroadcasts(prev => {
          const existingIds = prev.map(p => p.id);
          const newBroadcasts = response.data.pending_broadcasts.filter(
            b => !existingIds.includes(b.id)
          );
          if (newBroadcasts.length > 0) {
            playNotificationSound('broadcast');
          }
          return [...prev, ...newBroadcasts];
        });
      }
    } catch (error) {
      console.log('Failed to check pending acknowledgements:', error);
    }
  }, [api, user]);

  useEffect(() => {
    checkPendingAcknowledgements();
    const interval = setInterval(checkPendingAcknowledgements, 30000);
    return () => clearInterval(interval);
  }, [checkPendingAcknowledgements]);

  // Check for motivation messages periodically
  const checkMotivation = useCallback(async () => {
    if (!user || user.role === 'admin') return;
    
    try {
      const response = await api.get('/motivation/check');
      if (response.data.eligible && response.data.motivation) {
        setMotivation({
          ...response.data.motivation,
          rank: response.data.rank,
          totalTeam: response.data.total_team,
          todaySales: response.data.today_sales,
          todayRank: response.data.today_rank
        });
        playNotificationSound('motivation');
      }
    } catch (error) {
      console.log('Motivation check failed:', error);
    }
  }, [api, user]);

  useEffect(() => {
    checkMotivation();
    // Check every 15 minutes for more frequent motivation
    const interval = setInterval(checkMotivation, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [checkMotivation]);

  // Listen for WebSocket broadcast messages
  useEffect(() => {
    const handleBroadcast = (event) => {
      if (event.detail?.type === 'broadcast') {
        const newNotification = {
          id: event.detail.broadcast_id,
          title: event.detail.title,
          message: event.detail.message,
          priority: event.detail.priority,
          from: event.detail.from,
          sent_at: new Date().toISOString(),
          created_by_name: event.detail.from
        };
        setPendingBroadcasts(prev => {
          if (prev.some(p => p.id === newNotification.id)) return prev;
          return [newNotification, ...prev];
        });
        playNotificationSound('broadcast');
      }
    };
    
    const handleMotivation = (event) => {
      if (event.detail) {
        setMotivation({
          title: event.detail.title,
          message: event.detail.message,
          type: event.detail.motivation_type || 'motivation',
          rank: event.detail.rank,
          todaySales: event.detail.today_sales,
          todayRank: event.detail.today_rank
        });
        playNotificationSound('motivation');
      }
    };

    window.addEventListener('broadcast-notification', handleBroadcast);
    window.addEventListener('motivation-notification', handleMotivation);
    return () => {
      window.removeEventListener('broadcast-notification', handleBroadcast);
      window.removeEventListener('motivation-notification', handleMotivation);
    };
  }, []);

  // Acknowledge a broadcast
  const acknowledgeBroadcast = async (broadcastId) => {
    setAcknowledging(prev => ({ ...prev, [broadcastId]: true }));
    
    try {
      await api.post(`/broadcasts/${broadcastId}/acknowledge`);
      setPendingBroadcasts(prev => prev.filter(n => n.id !== broadcastId));
      toast.success('Broadcast acknowledged');
    } catch (error) {
      console.error('Failed to acknowledge:', error);
      toast.error('Failed to acknowledge broadcast');
    } finally {
      setAcknowledging(prev => ({ ...prev, [broadcastId]: false }));
    }
  };

  const dismissMotivation = () => {
    setMotivation(null);
  };

  const getMotivationIcon = (type) => {
    switch (type) {
      case 'celebration': return <Crown className="w-12 h-12 text-amber-500" />;
      case 'motivation': return <TrendingUp className="w-12 h-12 text-emerald-500" />;
      case 'encouragement': return <Zap className="w-12 h-12 text-cyan-500" />;
      case 'push': return <Target className="w-12 h-12 text-purple-500" />;
      case 'team_update': return <Trophy className="w-12 h-12 text-emerald-500" />;
      default: return <Star className="w-12 h-12 text-indigo-500" />;
    }
  };

  const getMotivationColors = (type) => {
    switch (type) {
      case 'celebration': return { bg: 'bg-gradient-to-br from-amber-50 to-yellow-100 dark:from-amber-950 dark:to-yellow-950', border: 'border-amber-300 dark:border-amber-700', icon: 'bg-gradient-to-br from-amber-100 to-yellow-200 dark:from-amber-900 dark:to-yellow-900' };
      case 'motivation': return { bg: 'bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-950 dark:to-green-950', border: 'border-emerald-300 dark:border-emerald-700', icon: 'bg-gradient-to-br from-emerald-100 to-green-200 dark:from-emerald-900 dark:to-green-900' };
      case 'encouragement': return { bg: 'bg-gradient-to-br from-cyan-50 to-blue-100 dark:from-cyan-950 dark:to-blue-950', border: 'border-cyan-300 dark:border-cyan-700', icon: 'bg-gradient-to-br from-cyan-100 to-blue-200 dark:from-cyan-900 dark:to-blue-900' };
      case 'push': return { bg: 'bg-gradient-to-br from-purple-50 to-pink-100 dark:from-purple-950 dark:to-pink-950', border: 'border-purple-300 dark:border-purple-700', icon: 'bg-gradient-to-br from-purple-100 to-pink-200 dark:from-purple-900 dark:to-pink-900' };
      case 'team_update': return { bg: 'bg-gradient-to-br from-indigo-50 to-violet-100 dark:from-indigo-950 dark:to-violet-950', border: 'border-indigo-300 dark:border-indigo-700', icon: 'bg-gradient-to-br from-indigo-100 to-violet-200 dark:from-indigo-900 dark:to-violet-900' };
      default: return { bg: 'bg-gradient-to-br from-slate-50 to-gray-100 dark:from-slate-950 dark:to-gray-950', border: 'border-slate-300 dark:border-slate-700', icon: 'bg-gradient-to-br from-slate-100 to-gray-200 dark:from-slate-900 dark:to-gray-900' };
    }
  };

  const getPriorityColors = (priority) => {
    switch (priority) {
      case 'urgent': return { bg: 'bg-red-50 dark:bg-red-950', border: 'border-red-300 dark:border-red-800', badge: 'bg-red-500' };
      case 'high': return { bg: 'bg-orange-50 dark:bg-orange-950', border: 'border-orange-300 dark:border-orange-800', badge: 'bg-orange-500' };
      default: return { bg: 'bg-indigo-50 dark:bg-indigo-950', border: 'border-indigo-300 dark:border-indigo-800', badge: 'bg-indigo-500' };
    }
  };

  // Check if there's any blocking notification
  const hasBlockingNotification = motivation || pendingBroadcasts.length > 0;

  if (!hasBlockingNotification) return null;

  return (
    <>
      {/* Full screen overlay - blocks all interaction */}
      <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm" />
      
      {/* Center Modal Container */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
        <div className="w-full max-w-lg animate-in zoom-in-95 duration-300">
          
          {/* Motivation Popup - Center Modal */}
          {motivation && (
            <div className={`relative rounded-2xl border-2 shadow-2xl overflow-hidden ${getMotivationColors(motivation.type).bg} ${getMotivationColors(motivation.type).border}`}>
              {/* Animated background effect */}
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-0 right-0 w-32 h-32 bg-white rounded-full blur-3xl animate-pulse delay-500" />
              </div>
              
              {/* Header with icon */}
              <div className="relative p-6 text-center">
                <div className={`w-24 h-24 mx-auto mb-4 rounded-2xl ${getMotivationColors(motivation.type).icon} flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform`}>
                  {getMotivationIcon(motivation.type)}
                </div>
                
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  {motivation.title}
                </h2>
                
                <p className="text-slate-700 dark:text-slate-300 text-lg leading-relaxed mb-4">
                  {motivation.message}
                </p>
                
                {/* Stats Row */}
                <div className="flex items-center justify-center gap-4 flex-wrap">
                  {motivation.rank && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-full shadow-sm border border-slate-200 dark:border-slate-700">
                      <Trophy className="w-4 h-4 text-amber-500" />
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Overall Rank: #{motivation.rank}
                      </span>
                    </div>
                  )}
                  
                  {motivation.todaySales !== undefined && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-full shadow-sm border border-slate-200 dark:border-slate-700">
                      <Zap className="w-4 h-4 text-indigo-500" />
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Today: {motivation.todaySales} sales
                      </span>
                    </div>
                  )}
                  
                  {motivation.todayRank && motivation.todayRank > 0 && (
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 rounded-full shadow-sm border border-slate-200 dark:border-slate-700">
                      <Star className="w-4 h-4 text-purple-500" />
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Today's Rank: #{motivation.todayRank}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Footer with acknowledge button */}
              <div className="relative p-4 border-t border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50">
                <Button
                  onClick={dismissMotivation}
                  className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold text-base rounded-xl shadow-lg transform hover:scale-[1.02] transition-all"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Let's Go! 🚀
                </Button>
              </div>
            </div>
          )}

          {/* Pending Broadcast - Center Modal (shows one at a time) */}
          {!motivation && pendingBroadcasts.length > 0 && (
            <div className={`relative rounded-2xl border-2 shadow-2xl ${getPriorityColors(pendingBroadcasts[0].priority).bg} ${getPriorityColors(pendingBroadcasts[0].priority).border}`}>
              {/* Priority Badge */}
              {pendingBroadcasts[0].priority === 'urgent' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1.5 bg-red-500 text-white text-xs font-bold uppercase tracking-wider rounded-full shadow-lg flex items-center gap-1.5">
                    <AlertCircle className="w-3.5 h-3.5" />
                    URGENT
                  </span>
                </div>
              )}
              
              {/* Header */}
              <div className="p-6 text-center pt-8">
                <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-white dark:bg-slate-800 shadow-lg flex items-center justify-center border border-slate-200 dark:border-slate-700">
                  <Megaphone className="w-10 h-10 text-indigo-600 dark:text-indigo-400" />
                </div>
                
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  {pendingBroadcasts[0].title}
                </h2>
                
                <p className="text-slate-600 dark:text-slate-300 text-base leading-relaxed mb-4">
                  {pendingBroadcasts[0].message}
                </p>
                
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  From: <span className="font-medium">{pendingBroadcasts[0].created_by_name || pendingBroadcasts[0].from}</span>
                </p>
                
                {pendingBroadcasts.length > 1 && (
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                    +{pendingBroadcasts.length - 1} more notification{pendingBroadcasts.length > 2 ? 's' : ''} waiting
                  </p>
                )}
              </div>
              
              {/* Footer with acknowledge button */}
              <div className="p-4 border-t border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 rounded-b-2xl">
                <Button
                  data-testid={`acknowledge-btn-${pendingBroadcasts[0].id}`}
                  onClick={() => acknowledgeBroadcast(pendingBroadcasts[0].id)}
                  disabled={acknowledging[pendingBroadcasts[0].id]}
                  className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-base rounded-xl shadow-lg"
                >
                  {acknowledging[pendingBroadcasts[0].id] ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Acknowledging...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      I've Read This Message
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationPopup;
